import { Link, useLocation } from '@tanstack/react-router'
import type { ParseKeys } from 'i18next'
import { AnimatePresence, motion } from 'motion/react'
import { createContext, useContext, useState } from 'react'
import type { ComponentProps, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { BrandLogo, BrandMark } from '@/components/common/brand/BrandLogo'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ActivePill, GlideArea, useGlideTarget } from '@/components/ui/glide'
import { Icon } from '@/components/ui/icon'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAccess } from '@/hooks/queries/access'
import { FOLD_SPRING, PILL_SPRING } from '@/lib/motion'
import { cn } from '@/lib/utils'

import {
  allSoon,
  containsPath,
  hasPages,
  isGroup,
  visibleFooter,
  matchesPath,
  trailFor,
  visibleRuns,
  type NavLink,
  type NavNode,
  type Section,
} from './navigation'

/** What every row of the tree needs to know, without threading it down. */
type Tree = {
  /** The link the user is on: the longest match, so a detail page keeps its list lit. */
  current: string | undefined
  isOpen: (key: ParseKeys, children: readonly NavNode[]) => boolean
  setOpen: (key: ParseKeys, open: boolean) => void
  /** Collapsed to icons on a desktop: first-level rows only, names in tooltips. */
  iconOnly: boolean
  /** A page was chosen: in the drawer, that is the end of the trip. */
  onNavigate: () => void
}

const TreeContext = createContext<Tree | null>(null)

function useTree(): Tree {
  const tree = useContext(TreeContext)
  if (!tree) throw new Error('A sidebar row must render inside AppSidebar.')
  return tree
}

/**
 * The sidebar, drawn as a file tree (design after ObsidianUI's dashboard
 * shell and Untitled UI's tree view). Sections are the first level, each in
 * its own hue — the one place in the app a hue is only a wayfinding aid.
 * Everything below them is grey, so the current page's crimson is the only
 * colour inside an open folder. A section of several pages is a folder that
 * opens in place, its pages hanging off a guide line under the folder's icon,
 * one group deep at most. Every row starts with its icon and label on one
 * line; a folder's chevron sits at the row's end.
 *
 * Motion carries the structure: a folder unfolds to its height rather than
 * popping open, its chevron turns, one hover highlight glides after the
 * pointer through the whole tree, and the current page's pill and guide mark
 * slide to the next page chosen. All of it stands still under reduced motion
 * (`MotionConfig` in the shell).
 *
 * A folder opens by itself when the user lands on one of its pages, so a deep
 * link never hides where they are; a folder the user opened or closed stays
 * that way.
 *
 * Collapsed (68px) it shows the sections as icons. Each keeps its name — for
 * a screen reader always, on hover and focus in a tooltip — and the section
 * holding the current page keeps the pill. Choosing a folder there widens the
 * sidebar to show it. Below 768px it is a drawer.
 *
 * A page not built yet keeps its row, marked Soon, as a disabled button: it
 * can be found and read, but never leads to not-found.
 *
 * Navigation uses `Link`, never an `<a href>`. An anchor triggers a full
 * document load, which throws away the React Query cache and makes an in-app
 * navigation slower than the first paint.
 */
export function AppSidebar() {
  const { t } = useTranslation()
  const pathname = useLocation({ select: (location) => location.pathname })
  const sidebar = useSidebar()
  const [chosen, setChosen] = useState<Partial<Record<ParseKeys, boolean>>>({})
  // Only what the reader holds something for (`visibleRuns`); everything
  // while that is still being read, or if it cannot be.
  const access = useAccess().data
  const runs = visibleRuns(access)

  const iconOnly = !sidebar.isMobile && sidebar.state === 'collapsed'
  const tree: Tree = {
    current: trailFor(pathname)?.page?.to,
    isOpen: (key, children) => chosen[key] ?? containsPath(children, pathname),
    setOpen: (key, open) => {
      setChosen((previous) => ({ ...previous, [key]: open }))
    },
    iconOnly,
    onNavigate: () => {
      if (sidebar.isMobile) sidebar.setOpenMobile(false)
    },
  }

  return (
    <TreeContext.Provider value={tree}>
      <Sidebar
        collapsible="icon"
        mobileTitle={t('nav.drawerTitle')}
        mobileDescription={t('nav.drawerDescription')}
      >
        <SidebarHeader className="px-3 pt-3 pb-1">
          {iconOnly ? (
            <ExpandButton />
          ) : (
            <div className="flex h-10 items-center gap-1">
              {/* The same place as Dashboard, one row below. Out of the tab
                  order and the accessibility tree, so a keyboard or screen
                  reader meets one home link, and one "current page", not two. */}
              <Link
                to="/"
                tabIndex={-1}
                aria-hidden="true"
                className="flex h-10 min-w-0 flex-1 items-center rounded-md px-2 transition-transform active:scale-[0.98]"
              >
                <BrandLogo className="h-7 w-auto" ink="fill-sidebar-foreground" />
              </Link>
              <CollapseButton />
            </div>
          )}
        </SidebarHeader>

        {/* The list scrolls with no bar drawn, so its foot fades instead: a
            row cut off by the footer's hairline reads as the end of the list. */}
        <SidebarContent className="scrollbar-none overflow-x-hidden mask-b-from-[calc(100%-1.5rem)]">
          <nav aria-label={t('nav.main')} className="px-2 pb-3">
            <GlideArea highlightClassName="bg-sidebar-hover rounded-md">
              {runs.map((run, index) => (
                // By index, not `first:`: the hover highlight is the glide
                // area's first child while it shows, and a `first:` padding
                // would drop the whole list 12px on the first hover.
                <div key={run.key ?? index} className={index === 0 ? 'pt-1' : 'pt-4'}>
                  {run.key && !iconOnly ? (
                    <p className="text-sidebar-muted tracking-overline text-2xs px-2 pb-1.5 font-bold uppercase">
                      {t(run.key)}
                    </p>
                  ) : null}
                  <ul className="flex flex-col gap-px">
                    {run.sections.map((section) => (
                      <SectionRow key={section.key} section={section} pathname={pathname} />
                    ))}
                  </ul>
                </div>
              ))}
            </GlideArea>
          </nav>
        </SidebarContent>

        <SidebarFooter className="px-2 pt-0 pb-3">
          <GlideArea
            className="border-sidebar-border border-t pt-2"
            highlightClassName="bg-sidebar-hover rounded-md"
          >
            <ul className="flex flex-col gap-px">
              {visibleFooter(access).map((section) => (
                <SectionRow key={section.key} section={section} pathname={pathname} />
              ))}
            </ul>
          </GlideArea>
        </SidebarFooter>

        <SidebarRail aria-label={t('nav.toggleSidebar')} title={t('nav.toggleSidebar')} />
      </Sidebar>
    </TreeContext.Provider>
  )
}

/** A first-level row: a section's page, or a section's folder. */
function SectionRow({ section, pathname }: { section: Section; pathname: string }) {
  const { t } = useTranslation()
  const tree = useTree()
  const { setOpen: setSidebarOpen } = useSidebar()
  const label = t(section.key)

  if (!hasPages(section)) {
    const icon = <Icon name={section.icon} className={cn('relative shrink-0', section.hue)} />
    if (section.soon) {
      return (
        <li>
          <WithTooltip label={t('nav.comingSoon', { name: label })}>
            <SoonRow label={label} depth={0}>
              {icon}
            </SoonRow>
          </WithTooltip>
        </li>
      )
    }
    const current = matchesPath(section.to, pathname)
    return (
      <li>
        <WithTooltip label={label}>
          <PageLink to={section.to} current={current} depth={0}>
            {icon}
            <Label>{label}</Label>
          </PageLink>
        </WithTooltip>
      </li>
    )
  }

  const open = !tree.iconOnly && tree.isOpen(section.key, section.children)
  // The folder holding the current page, when that page cannot be seen: in
  // the icon strip it takes the pill; open-able but shut, its name is lit.
  const holdsCurrent = containsPath(section.children, pathname)
  return (
    <li>
      <WithTooltip label={label}>
        <FolderButton
          open={open}
          depth={0}
          label={label}
          holdsCurrent={holdsCurrent && !open}
          pill={holdsCurrent && tree.iconOnly}
          onToggle={() => {
            if (tree.iconOnly) {
              // From the icon strip, a folder can only be shown by widening.
              setSidebarOpen(true)
              tree.setOpen(section.key, true)
            } else {
              tree.setOpen(section.key, !open)
            }
          }}
        >
          <Icon name={section.icon} className={cn('relative shrink-0', section.hue)} />
        </FolderButton>
      </WithTooltip>
      <Branch open={open} nodes={section.children} depth={1} />
    </li>
  )
}

/**
 * The rows under an open folder, hanging off its guide line. The line sits
 * under the centre of the folder's icon — 8px of padding plus half the icon:
 * 18 for a section's 20px icon, 16 for a nested folder's 16px one — so each
 * level's icons hang from the one that opened them.
 */
function Branch({
  open,
  nodes,
  depth,
  tagged = true,
}: {
  open: boolean
  nodes: readonly NavNode[]
  depth: number
  /** False under a folder that carries the Soon tag for all of them. */
  tagged?: boolean
}) {
  return (
    // No entrance on first paint: a folder open on load is simply open.
    <AnimatePresence initial={false}>
      {open ? (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ height: FOLD_SPRING, opacity: { duration: 0.18 } }}
          className="overflow-hidden"
        >
          <ul
            className={cn(
              'border-sidebar-border flex flex-col gap-px border-l py-0.5 pl-1.5',
              depth === 1 ? 'ml-4.25' : 'ml-3.75',
            )}
          >
            {nodes.map((node) => (
              <NodeRow
                key={isGroup(node) ? node.key : node.to}
                node={node}
                depth={depth}
                tagged={tagged}
              />
            ))}
          </ul>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

/** A row below the first level: a page, a page not built yet, or a nested folder. */
function NodeRow({ node, depth, tagged }: { node: NavNode; depth: number; tagged: boolean }) {
  const { t } = useTranslation()
  const tree = useTree()

  if (!isGroup(node)) {
    if (node.soon) {
      return (
        <li>
          <SoonRow label={t(node.key)} depth={depth} tagged={tagged}>
            <NodeIcon node={node} current={false} />
          </SoonRow>
        </li>
      )
    }
    const current = node.to === tree.current
    return (
      <li>
        <PageLink to={node.to} current={current} depth={depth}>
          {current ? (
            // The guide line's mark beside the current page, sliding with it.
            <motion.span
              aria-hidden="true"
              layoutId="nav-guide"
              transition={PILL_SPRING}
              className="bg-sidebar-indicator absolute top-1.5 bottom-1.5 -left-2 w-0.5 rounded-full"
            />
          ) : null}
          <NodeIcon node={node} current={current} />
          <Label>{t(node.key)}</Label>
        </PageLink>
      </li>
    )
  }

  const open = tree.isOpen(node.key, node.children)
  const soon = allSoon(node.children)
  return (
    <li>
      <FolderButton
        soon={soon}
        open={open}
        depth={depth}
        label={t(node.key)}
        holdsCurrent={
          !open && node.children.some((child) => !isGroup(child) && child.to === tree.current)
        }
        onToggle={() => tree.setOpen(node.key, !open)}
      >
        <Icon
          name={open ? 'folder_open' : 'folder'}
          size={16}
          className="text-sidebar-icon group-hover/row:text-sidebar-foreground relative shrink-0 transition-colors"
        />
      </FolderButton>
      <Branch open={open} nodes={node.children} depth={depth + 1} tagged={!soon} />
    </li>
  )
}

/** A page's 16px icon: grey at rest, the row's text colour on hover, crimson when current. */
function NodeIcon({ node, current }: { node: NavLink; current: boolean }) {
  if (!node.icon) return null
  return (
    <Icon
      name={node.icon}
      size={16}
      className={cn(
        'relative shrink-0 transition-colors',
        current
          ? 'text-sidebar-active-foreground'
          : 'text-sidebar-icon group-hover/row:text-sidebar-foreground',
      )}
    />
  )
}

/** Quiet on the rail, lit on hover: the header's icon buttons. */
const RAIL_BUTTON =
  'text-sidebar-icon hover:bg-sidebar-hover hover:text-sidebar-foreground active:bg-sidebar-hover'

/**
 * Beside the name: folds the sidebar to icons on a desktop, and closes the
 * drawer on a phone, where the topbar's button is what opened it.
 */
function CollapseButton() {
  const { t } = useTranslation()
  const { isMobile, toggleSidebar } = useSidebar()
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      className={RAIL_BUTTON}
      aria-label={isMobile ? t('nav.closeNavigation') : t('nav.collapseSidebar')}
      onClick={toggleSidebar}
    >
      <Icon name="left_panel_close" />
    </Button>
  )
}

/**
 * The icon strip has room for one square, so the logo is the way back out:
 * hover or focus turns it into the expand icon, which says what a click does.
 */
function ExpandButton() {
  const { t } = useTranslation()
  const { toggleSidebar } = useSidebar()
  const label = t('nav.expandSidebar')
  return (
    <WithTooltip label={label}>
      <ExpandTrigger label={label} onExpand={toggleSidebar} />
    </WithTooltip>
  )
}

function ExpandTrigger({
  label,
  onExpand,
  onClick,
  ...forwarded
}: { label: string; onExpand: () => void } & Forwarded<'button'>) {
  return (
    <button
      {...forwarded}
      type="button"
      aria-label={label}
      onClick={(event) => {
        onClick?.(event)
        onExpand()
      }}
      className="group/expand mx-auto flex size-10 items-center justify-center rounded-md"
    >
      <span className="relative flex size-7 items-center justify-center">
        <span className="transition-opacity duration-150 group-hover/expand:opacity-0 group-focus-visible/expand:opacity-0">
          <BrandMark className="size-7" ink="fill-sidebar-foreground" />
        </span>
        <span
          aria-hidden="true"
          className="bg-sidebar-hover text-sidebar-foreground absolute inset-0 flex items-center justify-center rounded-md opacity-0 transition-opacity duration-150 group-hover/expand:opacity-100 group-focus-visible/expand:opacity-100"
        >
          <Icon name="left_panel_open" />
        </span>
      </span>
    </button>
  )
}

/** Shared by every row: height, padding, the press, the hover glide. */
function rowClassName(depth: number, tone: 'current' | 'rest' | 'soon') {
  return cn(
    'group/row relative flex w-full items-center rounded-md px-2 text-left text-sm transition-[color,scale] duration-150',
    tone !== 'soon' && 'active:scale-[0.98]',
    depth === 0 ? 'h-9 gap-2.5 font-medium' : 'h-8 gap-2',
    'group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0',
    tone === 'current' && 'text-sidebar-active-foreground font-medium',
    tone === 'soon' && 'text-sidebar-muted cursor-default',
    tone === 'rest' &&
      (depth === 0
        ? 'text-sidebar-foreground'
        : 'text-sidebar-label hover:text-sidebar-foreground'),
  )
}

/**
 * What a row passes on to the element it renders: the tooltip trigger's
 * handlers, its `data-state` and its ref arrive here through `asChild`, and a
 * row that dropped them would have no tooltip at all.
 */
type Forwarded<E extends 'a' | 'button'> = Omit<ComponentProps<E>, 'children' | 'className'>

function PageLink({
  to,
  current,
  depth,
  children,
  onPointerEnter,
  onClick,
  ...forwarded
}: {
  to: string
  current: boolean
  depth: number
  children: ReactNode
} & Forwarded<'a'>) {
  const tree = useTree()
  const glide = useGlideTarget()
  return (
    <Link
      {...forwarded}
      to={to}
      // Exact, so `/marketing/rules-v2` is not marked current while the user
      // is on its executions page beside it, and `/` not on every page. The
      // query string is not compared: a page's tab or filters (`?tab=costs`)
      // are still that page.
      activeOptions={{ exact: true, includeSearch: false }}
      // The router marks only the exact page. A detail route (`/tickets/42`)
      // keeps its list lit, so it keeps its list current for a screen reader
      // too.
      aria-current={current ? 'page' : undefined}
      onPointerEnter={(event) => {
        onPointerEnter?.(event)
        glide(event)
      }}
      onClick={(event) => {
        onClick?.(event)
        tree.onNavigate()
      }}
      className={rowClassName(depth, current ? 'current' : 'rest')}
    >
      {current ? <SidebarPill /> : null}
      {children}
    </Link>
  )
}

function SidebarPill() {
  return (
    <ActivePill
      layoutId="nav-pill"
      className="bg-sidebar-active ring-sidebar-border rounded-md ring-1"
    />
  )
}

/**
 * A folder: a button, not a link — it has no page of its own, and an anchor
 * that only toggles would be announced as a link that goes nowhere.
 */
function FolderButton({
  open,
  depth,
  label,
  onToggle,
  holdsCurrent = false,
  pill = false,
  soon = false,
  children,
  onPointerEnter,
  onClick,
  ...forwarded
}: {
  open: boolean
  depth: number
  label: string
  onToggle: () => void
  /** Shut over the current page: its name takes the current page's colour. */
  holdsCurrent?: boolean
  /** In the icon strip, over the current page: it takes the pill too. */
  pill?: boolean
  /** Every page inside is still to come: the folder carries the one Soon tag. */
  soon?: boolean
  /** The folder's icon. */
  children: ReactNode
} & Forwarded<'button'>) {
  const glide = useGlideTarget()
  return (
    <button
      {...forwarded}
      type="button"
      aria-expanded={open}
      onClick={(event) => {
        onClick?.(event)
        onToggle()
      }}
      onPointerEnter={(event) => {
        onPointerEnter?.(event)
        glide(event)
      }}
      className={rowClassName(depth, holdsCurrent || pill ? 'current' : 'rest')}
    >
      {pill ? <SidebarPill /> : null}
      {children}
      <Label>{label}</Label>
      {soon ? <SoonTag /> : null}
      <Twisty open={open} />
    </button>
  )
}

/**
 * A page not built yet: in the map, so the rail says what is coming, and
 * readable by keyboard and screen reader, but a disabled button rather than a
 * link — choosing it does nothing, so nothing ends on not-found.
 */
function SoonRow({
  label,
  depth,
  tagged = true,
  children,
  onPointerEnter,
  ...forwarded
}: {
  label: string
  depth: number
  /** False under a folder whose tag already says it. */
  tagged?: boolean
  /** The page's icon. */
  children: ReactNode
} & Forwarded<'button'>) {
  const { t } = useTranslation()
  const glide = useGlideTarget()
  return (
    <button
      {...forwarded}
      type="button"
      aria-disabled="true"
      aria-label={t('nav.comingSoon', { name: label })}
      onPointerEnter={(event) => {
        onPointerEnter?.(event)
        glide(event)
      }}
      className={rowClassName(depth, 'soon')}
    >
      {children}
      <Label>{label}</Label>
      {tagged ? <SoonTag /> : null}
    </button>
  )
}

/** "Soon", on a page not built yet or a folder of them. The row's name says it to a screen reader. */
function SoonTag() {
  const { t } = useTranslation()
  return (
    <Badge
      variant="outline"
      size="sm"
      aria-hidden="true"
      className="border-sidebar-border text-sidebar-muted relative bg-transparent group-data-[collapsible=icon]:hidden"
    >
      {t('nav.soon')}
    </Badge>
  )
}

/**
 * A folder's chevron, at the end of its row so every icon and label in a
 * level starts on the same line, page or folder. It turns as the folder
 * opens.
 */
function Twisty({ open }: { open: boolean }) {
  return (
    <motion.span
      aria-hidden="true"
      className="text-sidebar-muted group-hover/row:text-sidebar-foreground relative flex size-4 shrink-0 transition-colors group-data-[collapsible=icon]:hidden"
      initial={false}
      animate={{ rotate: open ? 90 : 0 }}
      transition={PILL_SPRING}
    >
      <Icon name="chevron_right" size={16} />
    </motion.span>
  )
}

/** A row's name. In the icon strip it is only hidden from sight, so the row keeps its name. */
function Label({ children }: { children: ReactNode }) {
  return (
    <span className="relative min-w-0 flex-1 truncate group-data-[collapsible=icon]:sr-only">
      {children}
    </span>
  )
}

/** A name on hover, only while the sidebar is icons and the name is hidden. */
function WithTooltip({ label, children }: { label: string; children: ReactNode }) {
  const tree = useTree()
  if (!tree.iconOnly) return children
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  )
}
